import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Search, Shield, User, Plus, X, Eye, EyeOff, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../apiBase';

const getPhotoUrl = (photo_url) => {
  if (!photo_url) return null;
  // Si c'est du base64, retourner tel quel
  if (photo_url.startsWith('data:')) return photo_url;
  // Si c'est une URL externe, retourner tel quel
  if (photo_url.startsWith('http')) return photo_url;
  // Sinon, ajouter l'URL du backend
  return `${API_URL}${photo_url}`;
};

const getInitials = (nom, prenom) => {
  const n = nom?.charAt(0) || '';
  const p = prenom?.charAt(0) || '';
  return `${p}${n}`.toUpperCase();
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.startsWith('33') && cleaned.length === 11) {
    return `+33 ${cleaned[2]} ${cleaned.substring(3, 5)} ${cleaned.substring(5, 7)} ${cleaned.substring(7, 9)} ${cleaned.substring(9, 11)}`;
  }
  
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    return `+33 ${cleaned[1]} ${cleaned.substring(2, 4)} ${cleaned.substring(4, 6)} ${cleaned.substring(6, 8)} ${cleaned.substring(8, 10)}`;
  }
  
  return phone;
};

const UsersManagement = ({ usersSubTab = 'users' }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortColumn, setSortColumn] = useState('utilisateur');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  
  // Utilise le sous-tab passé en prop depuis la sidebar
  const activeTab = usersSubTab === 'permissions' ? 'permissions' : 'utilisateurs';
  
  // Permissions management
  const [userPermissions, setUserPermissions] = useState({}); // { userId: { perm1: true, perm2: false, ... } }
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showUserPermissionsModal, setShowUserPermissionsModal] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissionSearchTerm, setPermissionSearchTerm] = useState('');
  
  // Define available permissions
  const permissions = [
    {
      id: 'orgchart_modify_layout',
      category: 'Organigramme',
      label: 'Autorisation de modifier la disposition des utilisateurs',
      description: 'Permet de déplacer et réorganiser les utilisateurs dans l\'organigramme'
    },
    {
      id: 'mailing_create_predefined',
      category: 'Listes de Diffusion',
      label: 'Autorisation de création de listes Prédéfinies',
      description: 'Permet de créer des listes de diffusion globales accessibles à tous les utilisateurs'
    }
  ];

  useEffect(() => {
    fetchUsers();
    fetchAllPermissions();
  }, []);

  useEffect(() => {
    filterUsers();
    setCurrentPage(1);
  }, [users, searchTerm, roleFilter, sortColumn, sortDirection]);

  const fetchUsers = async () => {
    try {
      // Pour l'admin, inclure les utilisateurs masqués
      const response = await axios.get(`${API_URL}/api/users?include_hidden=true`);
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
      setLoading(false);
    }
  };

  const fetchAllPermissions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/permissions/`);
      setUserPermissions(response.data.permissions || {});
    } catch (error) {
      console.error('Erreur lors du chargement des permissions:', error);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.nom?.toLowerCase().includes(term) ||
        user.prenom?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term)
      );
    }

    if (roleFilter === 'admin') {
      filtered = filtered.filter(user => user.is_admin === true);
    } else if (roleFilter === 'user') {
      filtered = filtered.filter(user => !user.is_admin);
    }

    filtered = sortUsers(filtered);

    setFilteredUsers(filtered);
  };

  const sortUsers = (usersToSort) => {
    return [...usersToSort].sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'utilisateur':
          comparison = `${a.prenom || ''} ${a.nom || ''}`.toLowerCase()
            .localeCompare(`${b.prenom || ''} ${b.nom || ''}`.toLowerCase());
          break;
        case 'role':
          comparison = Number(b.is_admin) - Number(a.is_admin);
          break;
        case 'annuaire':
          comparison = Number(b.show_in_directory !== false) - Number(a.show_in_directory !== false);
          break;
        default:
          comparison = `${a.prenom || ''} ${a.nom || ''}`.toLowerCase()
            .localeCompare(`${b.prenom || ''} ${b.nom || ''}`.toLowerCase());
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const adminCount = users.filter(u => u.is_admin).length;
  const userCount = users.filter(u => !u.is_admin).length;
  const visibleInDirectoryCount = users.filter(u => u.show_in_directory !== false).length;

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

  // Permission management functions
  const toggleUserPermission = async (userId, permissionId) => {
    const currentUserPerms = userPermissions[userId] || {};
    const updatedUserPerms = {
      ...currentUserPerms,
      [permissionId]: !currentUserPerms[permissionId]
    };
    
    try {
      // Save to backend
      await axios.put(`${API_URL}/api/permissions/${userId}`, {
        user_id: userId,
        permissions: updatedUserPerms
      });
      
      // Update local state
      setUserPermissions(prev => ({
        ...prev,
        [userId]: updatedUserPerms
      }));
      
      toast.success('Permissions mises à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour des permissions');
      console.error('Permission update error:', error);
    }
  };

  const getUsersWithPermission = (permissionId) => {
    return users.filter(user => userPermissions[user.id]?.[permissionId]);
  };

  const handleAddUsersToPermission = (permissionId) => {
    setSelectedPermission(permissionId);
    setShowPermissionModal(true);
    setPermissionSearchTerm('');
  };

  const handleManageUserPermissions = (user) => {
    setSelectedUser(user);
    setShowUserPermissionsModal(true);
  };

  const toggleDirectoryVisibility = async (e, user) => {
    e.stopPropagation(); // Empêcher l'ouverture de la modal
    const newVisibility = !user.show_in_directory;
    
    try {
      await axios.patch(`${API_URL}/api/users/${user.id}/directory-visibility`, {
        show_in_directory: newVisibility
      });
      
      // Mettre à jour l'état local
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === user.id ? { ...u, show_in_directory: newVisibility } : u
        )
      );
      
      toast.success(newVisibility 
        ? `${user.prenom} ${user.nom} est maintenant visible dans l'annuaire`
        : `${user.prenom} ${user.nom} est masqué de l'annuaire`
      );
    } catch (error) {
      toast.error('Erreur lors de la mise à jour de la visibilité');
      console.error('Directory visibility update error:', error);
    }
  };

  const filteredUsersForPermission = users.filter(user => {
    const search = permissionSearchTerm.toLowerCase();
    const fullName = `${user.prenom} ${user.nom}`.toLowerCase();
    const email = (user.email || '').toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs Navigation */}
      {/* Content based on active tab (contrôlé via sidebar) */}
      {activeTab === 'utilisateurs' ? (
        <>
      {/* Header Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-lg">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Utilisateurs</p>
              <p className="text-2xl font-bold">{users.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <Shield className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Administrateurs</p>
              <p className="text-2xl font-bold">{adminCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <User className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Utilisateurs Standard</p>
              <p className="text-2xl font-bold">{userCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-500/10 rounded-lg">
              <Eye className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Visibles dans l'annuaire</p>
              <p className="text-2xl font-bold">{visibleInDirectoryCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom, prénom ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="Filtrer par rôle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les rôles</SelectItem>
              <SelectItem value="admin">Administrateurs</SelectItem>
              <SelectItem value="user">Utilisateurs</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mt-3 text-sm text-muted-foreground">
          {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''} trouvé{filteredUsers.length > 1 ? 's' : ''}
        </div>
      </div>

      {/* Users List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th
                  className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none"
                  onClick={() => handleSort('utilisateur')}
                >
                  <div className="flex items-center gap-2">
                    <span>Utilisateur</span>
                    {sortColumn === 'utilisateur' ? (
                      sortDirection === 'asc' ?
                        <ChevronUp className="h-4 w-4" /> :
                        <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronsUpDown className="h-4 w-4 opacity-30" />
                    )}
                  </div>
                </th>
                <th
                  className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none"
                  onClick={() => handleSort('role')}
                >
                  <div className="flex items-center gap-2">
                    <span>Rôle</span>
                    {sortColumn === 'role' ? (
                      sortDirection === 'asc' ?
                        <ChevronUp className="h-4 w-4" /> :
                        <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronsUpDown className="h-4 w-4 opacity-30" />
                    )}
                  </div>
                </th>
                <th
                  className="text-center px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none"
                  onClick={() => handleSort('annuaire')}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>Annuaire</span>
                    {sortColumn === 'annuaire' ? (
                      sortDirection === 'asc' ?
                        <ChevronUp className="h-4 w-4" /> :
                        <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronsUpDown className="h-4 w-4 opacity-30" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paginatedUsers.map(user => (
                <tr
                  key={user.id}
                  onClick={() => handleManageUserPermissions(user)}
                  className="hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={getPhotoUrl(user.photo_url)} />
                        <AvatarFallback>{getInitials(user.nom, user.prenom)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {user.prenom} {user.nom}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.is_admin ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-500 text-sm font-medium">
                        <Shield className="h-4 w-4" />
                        Administrateur
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-muted text-muted-foreground text-sm font-medium">
                        <User className="h-4 w-4" />
                        Utilisateur
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={(e) => toggleDirectoryVisibility(e, user)}
                      className={`inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg transition-all ${
                        user.show_in_directory !== false
                          ? 'bg-green-500/10 text-green-600 hover:bg-green-500/20'
                          : 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
                      }`}
                      data-testid={`directory-visibility-toggle-${user.id}`}
                    >
                      {user.show_in_directory !== false ? (
                        <>
                          <Eye className="h-4 w-4" />
                          <span className="text-sm font-medium">Visible</span>
                        </>
                      ) : (
                        <>
                          <EyeOff className="h-4 w-4" />
                          <span className="text-sm font-medium">Masqué</span>
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-6 py-4">
            <div className="text-sm text-muted-foreground">
              Affichage de {startIndex + 1} à {Math.min(endIndex, filteredUsers.length)} sur {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                title="Première page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                title="Page précédente"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <div className="px-4 py-2 text-sm">
                Page <span className="font-medium">{currentPage}</span> sur <span className="font-medium">{totalPages}</span>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                title="Page suivante"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                title="Dernière page"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Aucun utilisateur trouvé</p>
        </div>
      )}
        </>
      ) : (
        /* Permissions View */
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Gestion des Permissions
                </h3>
                <p className="text-sm text-muted-foreground">
                  Configurez les droits d'accès pour l'application Annuaire Interne
                </p>
              </div>
            </div>
          </div>

          {/* Permissions List - DYNAMICALLY generate sections */}
          <div className="space-y-4">
            {[...new Set(permissions.map(p => p.category))].map((category, index) => (
              <div key={category} className="bg-card border border-border rounded-lg p-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-base font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {index + 1}. {category}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Permissions relatives à {category.toLowerCase()}
                    </p>
                  </div>

                  <div className="space-y-3 pl-4 border-l-2 border-border">
                    {permissions.filter(p => p.category === category).map(permission => {
                    const usersWithPerm = getUsersWithPermission(permission.id);
                    return (
                      <div key={permission.id} className="p-4 bg-muted/30 rounded-lg space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="font-medium text-sm mb-1">{permission.label}</div>
                            <p className="text-xs text-muted-foreground">{permission.description}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-2"
                            onClick={() => handleAddUsersToPermission(permission.id)}
                          >
                            <Plus className="h-4 w-4" />
                            Ajouter
                          </Button>
                        </div>
                        
                        {usersWithPerm.length > 0 && (
                          <div className="pt-3 border-t border-border">
                            <div className="text-xs font-medium text-muted-foreground mb-2">
                              {usersWithPerm.length} utilisateur{usersWithPerm.length > 1 ? 's' : ''} autorisé{usersWithPerm.length > 1 ? 's' : ''}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {usersWithPerm.slice(0, 5).map(user => (
                                <div
                                  key={user.id}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs"
                                >
                                  {user.prenom} {user.nom}
                                  <button
                                    onClick={() => toggleUserPermission(user.id, permission.id)}
                                    className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              ))}
                              {usersWithPerm.length > 5 && (
                                <span className="text-xs text-muted-foreground">+{usersWithPerm.length - 5} autres</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            ))}
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
            <div className="flex gap-3">
              <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                  Information
                </p>
                <p className="text-blue-700 dark:text-blue-300">
                  Les permissions définies ici s'appliquent à tous les utilisateurs. Pour des droits spécifiques par utilisateur, utilisez l'onglet "Utilisateurs".
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Add Users to Permission */}
      <Dialog open={showPermissionModal} onOpenChange={setShowPermissionModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ajouter des utilisateurs</DialogTitle>
            <DialogDescription>
              {selectedPermission && permissions.find(p => p.id === selectedPermission)?.label}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un utilisateur..."
                value={permissionSearchTerm}
                onChange={(e) => setPermissionSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Users list with checkboxes */}
            <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-2">
              {filteredUsersForPermission.map(user => {
                const hasPermission = userPermissions[user.id]?.[selectedPermission];
                return (
                  <div
                    key={user.id}
                    onClick={() => toggleUserPermission(user.id, selectedPermission)}
                    className="flex items-center gap-3 p-3 hover:bg-muted rounded-lg cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={hasPermission || false}
                      onChange={() => {}}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={getPhotoUrl(user.photo_url)} />
                      <AvatarFallback>{getInitials(user.nom, user.prenom)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{user.prenom} {user.nom}</div>
                      <div className="text-xs text-muted-foreground">{user.email}</div>
                    </div>
                    {user.is_admin && (
                      <span className="text-xs px-2 py-1 rounded-full bg-blue-500/10 text-blue-500">
                        Admin
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermissionModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Manage User Permissions */}
      <Dialog open={showUserPermissionsModal} onOpenChange={setShowUserPermissionsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gérer les permissions</DialogTitle>
            <DialogDescription>
              {selectedUser && `${selectedUser.prenom} ${selectedUser.nom}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Group permissions by category - DYNAMICALLY extract unique categories */}
            <div className="space-y-6">
              {[...new Set(permissions.map(p => p.category))].map(category => (
                <div key={category}>
                  <h4 className="font-semibold text-sm mb-3">{category}</h4>
                  <div className="space-y-2">
                    {permissions.filter(p => p.category === category).map(permission => {
                      const hasPermission = selectedUser && userPermissions[selectedUser.id]?.[permission.id];
                      return (
                        <div
                          key={permission.id}
                          onClick={() => selectedUser && toggleUserPermission(selectedUser.id, permission.id)}
                          className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={hasPermission || false}
                            onChange={() => {}}
                            className="h-4 w-4 rounded border-gray-300 mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm">{permission.label}</div>
                            <p className="text-xs text-muted-foreground mt-1">{permission.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserPermissionsModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagement;
